import React, { useState, useEffect } from "react";
import {
  Alert, Autocomplete, Box, Button,
  Checkbox, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, FormControlLabel, InputLabel, MenuItem, Select,
  Switch, Typography,
} from "@mui/material";
import { SearchRounded, RouterRounded } from "@mui/icons-material";
import api from "../../../../services/api";
import OltSelect from "../../../../components/OltSelect";
import LineSrvSelect from "../../../../components/LineSrvSelect";
import CustomTextField from "../../../../components/CustomTextField";
import ProvisioningProgressModal from "../../../../components/ProvisioningProgressModal";
import { useFormError } from "../../../../components/Datatable/FormErrorProvider";
import { useNotify } from "../../../../components/Datatable/NotifyProvider";


function genTaskId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID().replace(/-/g, "").slice(0, 8);
  return Math.random().toString(16).slice(2, 10);
}



export default function ProvGlobal() {
  const error = useFormError();
  const { setNotify } = useNotify();

  const [allOlts, setAllOlts] = useState(true);
  const [olt, setOlt] = useState(null);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null); // [{olt, name, ip, onus, cached, error, loading}]
  const [filter, setFilter] = useState(""); // filtro client-side dos SNs

  // SN selecionado para provisionar
  const [pick, setPick] = useState(null); // {olt, name, board, port, sn}
  const [profile, setProfile] = useState(null);
  const [roteada, setRoteada] = useState(1);
  const [description, setDescription] = useState("");

  // modo avançado: sem template manda line/srv/vlan/gemport na mão (via prov/detail)
  const [advanced, setAdvanced] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [line, setLine] = useState(null);
  const [srv, setSrv] = useState(null);
  const [vlan, setVlan] = useState("");
  const [gemport, setGemport] = useState("");

  const [taskId, setTaskId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [result, setResult] = useState(null);


  // dispara UMA requisição por OLT em paralelo e renderiza cada card assim que
  // responde — em vez de esperar todas. Cada request acerta o cache por OLT.
  const fetchOlt = (o) => {
    api.post(`provisioning/olt/onu/free_all`, { olt: o.olt })
      .then(res => {
        const r = Array.isArray(res.data) ? res.data[0] : null;
        setData(prev => (prev || []).map(x => x.olt === o.olt
          ? { ...x, ...(r || {}), olt: o.olt, name: x.name || r?.name, loading: false }
          : x));
      })
      .catch(e => {
        setData(prev => (prev || []).map(x => x.olt === o.olt
          ? { ...x, loading: false, error: e?.message || String(e) }
          : x));
      });
  };

  const search = async () => {
    error.setErrors([]);
    setFilter("");
    setLoading(true);
    setData(null);
    try {
      let olts;
      if (allOlts) {
        const res = await api.get(`provisioning/olt/prov_serv/olt/all/`);
        olts = (res.data || []).map(o => ({ olt: o.id, name: o.name, ip: o.ip }));
      } else {
        olts = [{ olt, name: null, ip: null }];
      }
      // placeholders já visíveis (cada um "carregando") e dispara em paralelo
      setData(olts.map(o => ({ ...o, onus: [], error: null, cached: false, loading: true })));
      olts.forEach(fetchOlt);
    } catch (e) {
      setNotify({ open: true, severity: "error", duration: 6000, message: "Falha na busca: " + (e?.message || e) });
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const matchOnu = (o) => {
    const q = filter.trim().toLowerCase();
    if (!q) return true;
    return [o.sn, o.model, o.vendor].some(v => (v || "").toLowerCase().includes(q));
  };

  const openPick = (oltResult, onu) => {
    setPick({ olt: oltResult.olt, name: oltResult.name, board: onu.board, port: onu.port, sn: onu.sn });
  };

  // ao abrir o dialog: reseta campos e busca os templates. Sem template →
  // liga o modo avançado automaticamente. Com template → mostra a lista.
  useEffect(() => {
    if (!pick) return;
    setProfile(null); setLine(null); setSrv(null);
    setVlan(""); setGemport(""); setRoteada(1); setDescription("");
    setProfilesLoading(true);
    api.get(`provisioning/profile/select/all`)
      .then(res => {
        const seen = new Set();
        const list = (res.data || []).filter(p => !seen.has(p.id) && seen.add(p.id));
        setProfiles(list);
        setAdvanced(list.length === 0);
      })
      .catch(() => { setProfiles([]); setAdvanced(true); })
      .finally(() => setProfilesLoading(false));
  }, [pick]);

  const provision = () => {
    error.setErrors([]);
    const tid = genTaskId();
    setTaskId(tid);
    setResult(null);
    setModalOpen(true);
    const snap = { ...pick };
    const adv = advanced;
    const url = adv
      ? `provisioning/olt/prov/detail/?task_id=${tid}`
      : `provisioning/olt/prov/global/?task_id=${tid}`;
    const body = adv
      ? { olt: snap.olt, board: snap.board, port: snap.port, sn: snap.sn,
          srv, line, vlan, gemport, description, roteada: roteada == 2 }
      : { olt: snap.olt, board: snap.board, port: snap.port, sn: snap.sn,
          profile, description, roteada: roteada == 2 };
    api.post(url, body)
      .then(res => {
        if (res.data.error) { setResult({ ok: false, message: res.data.error }); return; }
        if (res.data.errors) { setModalOpen(false); error.setErrors(res.data.errors); return; }
        setResult({
          ok: true,
          message: "ONU provisionada com sucesso",
          info: [
            { label: "SN", value: snap.sn },
            { label: "OLT", value: snap.name },
            { label: "Placa/Porta", value: `0/${snap.board}/${snap.port}` },
            ...(adv ? [{ label: "VLAN", value: vlan }, { label: "Gemport", value: gemport }] : []),
            { label: "Modo", value: roteada == 2 ? "Roteada" : "Bridge" },
          ],
        });
        // tira o SN provisionado da lista local na hora
        setData(prev => (prev || []).map(o =>
          o.olt === snap.olt ? { ...o, onus: (o.onus || []).filter(x => x.sn !== snap.sn) } : o
        ));
        setPick(null);
      })
      .catch(e => setResult({ ok: false, message: "Falha: " + (e?.message || e) }));
  };

  const canProvision = advanced ? (srv && line && vlan && gemport) : !!profile;

  const totalSns = (data || []).reduce((acc, o) => acc + (o.onus?.length || 0), 0);
  const anyLoading = (data || []).some(o => o.loading);
  const loadedOlts = (data || []).filter(o => !o.loading).length;
  // achata todas as ONUs num grid só (cada uma com a sua OLT), aplica o filtro
  const allOnus = (data || []).flatMap(o =>
    (o.onus || []).map(onu => ({ ...onu, olt: o.olt, olt_name: o.name })));
  const visibleOnus = allOnus.filter(matchOnu);
  const errorOlts = (data || []).filter(o => o.error);

  return (
    <Box display="flex" flexDirection="column" gap={2}>

      {/* barra de busca */}
      <Box display="flex" gap={1} flexDirection={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }}>
        <FormControlLabel
          control={<Checkbox checked={allOlts} onChange={e => setAllOlts(e.target.checked)} />}
          label="Todas as OLTs"
        />
        {!allOlts && (
          <OltSelect
            size="small"
            value={olt}
            handler={(e) => setOlt(e)}
            admin={false}
            sx={{ minWidth: 220, flexGrow: 1 }}
          />
        )}
        <Button
          variant="contained"
          startIcon={(loading || anyLoading) ? <CircularProgress size={16} color="inherit" /> : <SearchRounded />}
          onClick={search}
          disabled={loading || anyLoading || (!allOlts && !olt)}
        >
          Buscar SN disponíveis
        </Button>
      </Box>

      {data && (
        <Box display="flex" gap={1} flexDirection={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }}>
          <CustomTextField
            size="small"
            label="Filtrar SN / modelo"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            sx={{ minWidth: 240 }}
          />
          <Typography variant="body2" color="text.secondary">
            {totalSns} SN(s) em autofind
            {allOlts ? ` — ${loadedOlts}/${data.length} OLTs` : ""}
            {anyLoading ? " (consultando...)" : ""}
          </Typography>
        </Box>
      )}

      {/* OLTs que falharam (compacto) */}
      {errorOlts.length > 0 && (
        <Alert severity="warning" sx={{ py: 0 }}>
          {errorOlts.length} OLT(s) com erro: {errorOlts.map(o => o.name || `OLT ${o.olt}`).join(", ")}
        </Alert>
      )}

      {/* grid plano de TODAS as ONUs disponíveis — máx 4 por linha */}
      {data && visibleOnus.length === 0 && !anyLoading && (
        <Typography variant="body2" color="text.secondary">
          {totalSns === 0 ? "Nenhuma ONU aguardando autorização." : "Nenhum SN corresponde ao filtro."}
        </Typography>
      )}

      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: "repeat(4, 1fr)",
          },
        }}
      >
        {visibleOnus.map(onu => (
          <Button
            key={`${onu.olt}-${onu.board}-${onu.port}-${onu.sn}`}
            onClick={() => openPick({ olt: onu.olt, name: onu.olt_name }, onu)}
            variant="outlined"
            sx={{
              textTransform: "none",
              alignItems: "stretch",
              flexDirection: "column",
              gap: 0.5,
              p: 1.5,
              color: "text.primary",
              borderColor: "divider",
              "&:hover": { borderColor: "primary.main", backgroundColor: "action.hover" },
            }}
          >
            <Box display="flex" alignItems="center" gap={1} width="100%">
              <RouterRounded fontSize="small" sx={{ color: "primary.main" }} />
              <Typography fontFamily="monospace" fontWeight={700} fontSize="1.05rem" noWrap>
                {onu.sn}
              </Typography>
            </Box>
            <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.25} width="100%">
              <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: "100%" }}>
                <b>OLT:</b> {onu.olt_name}
              </Typography>
              <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                placa/porta&nbsp;0/{onu.board}/{onu.port}
              </Typography>
              {(onu.vendor || onu.model) && (
                <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: "100%" }}>
                  {[onu.vendor, onu.model].filter(Boolean).join(" · ")}
                </Typography>
              )}
              {onu.sw_version && (
                <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: "100%" }}>
                  fw {onu.sw_version}{onu.version ? ` (${onu.version})` : ""}
                </Typography>
              )}
              {onu.name && (
                <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: "100%" }}>
                  {onu.name}
                </Typography>
              )}
              {onu.autofind_time && (
                <Typography variant="caption" color="text.disabled" noWrap sx={{ maxWidth: "100%" }}>
                  visto: {onu.autofind_time}
                </Typography>
              )}
            </Box>
          </Button>
        ))}
      </Box>

      {/* dialog de provisionamento do SN escolhido */}
      <Dialog open={!!pick} onClose={() => setPick(null)} fullWidth maxWidth="sm">
        <DialogTitle>Provisionar SN</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Typography variant="body2" color="text.secondary">
              {pick?.name} — <Box component="span" fontFamily="monospace">0/{pick?.board}/{pick?.port}</Box>
            </Typography>
            <Typography fontFamily="monospace" fontWeight={600} fontSize="1.1rem">{pick?.sn}</Typography>

            <FormControlLabel
              control={<Switch checked={advanced} onChange={e => setAdvanced(e.target.checked)} />}
              label="Modo avançado"
            />

            {!advanced ? (
              <Autocomplete
                size="small"
                options={profiles}
                loading={profilesLoading}
                loadingText="Buscando..."
                noOptionsText="Nenhum profile cadastrado"
                getOptionLabel={(o) => o.description || ""}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
                value={profiles.find(p => p.id === profile) || null}
                onChange={(e, v) => setProfile(v?.id ?? null)}
                renderInput={(params) => (
                  <CustomTextField {...params} size="small" label="Profiles" />
                )}
              />
            ) : (
              <>
                <LineSrvSelect
                  fullWidth
                  size="small"
                  olt={pick?.olt}
                  valueLine={line}
                  valueSrv={srv}
                  handlerLine={(v) => setLine(v)}
                  handlerSrv={(v) => setSrv(v)}
                  callbackAfterRequest={() => {}}
                />
                <Box display="flex" gap={1}>
                  <CustomTextField
                    size="small" label="VLAN" type="number" value={vlan}
                    onChange={e => setVlan(e.target.value)} sx={{ flex: 1 }}
                  />
                  <CustomTextField
                    size="small" label="Gemport" type="number" value={gemport}
                    onChange={e => setGemport(e.target.value)} sx={{ flex: 1 }}
                  />
                </Box>
              </>
            )}

            <FormControl fullWidth size="small">
              <InputLabel id="global-mode-label">Modo</InputLabel>
              <Select
                labelId="global-mode-label"
                value={roteada}
                label="Modo"
                onChange={e => setRoteada(e.target.value)}
              >
                <MenuItem value={1}>Bridge</MenuItem>
                <MenuItem value={2}>Roteada</MenuItem>
              </Select>
            </FormControl>
            <CustomTextField
              fullWidth
              size="small"
              label="Descrição"
              value={description}
              onChange={e => setDescription(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPick(null)}>Cancelar</Button>
          <Button variant="contained" disabled={!canProvision} onClick={provision}>Provisionar</Button>
        </DialogActions>
      </Dialog>

      <ProvisioningProgressModal
        open={modalOpen}
        taskId={taskId}
        mode="user"
        streamPath="/provisioning/stream"
        result={result}
        onClose={() => { setModalOpen(false); setResult(null); }}
      />
    </Box>
  );
}
